<template>
  <article
    class="questions-panel__card"
    :class="{ 'questions-panel__card--unanswered': isUnanswered }"
  >
    <div
      class="questions-panel__question-header"
      :class="{ 'questions-panel__question-header--media': media }"
    >
      <button
        type="button"
        class="questions-panel__question"
        :aria-expanded="question.expanded ? 'true' : 'false'"
        :aria-controls="`question-${question.id}-answer`"
        :aria-invalid="isUnanswered ? 'true' : 'false'"
        :aria-describedby="isUnanswered ? `question-${question.id}-unanswered` : null"
        @click="emitToggle"
      >
        <span class="questions-panel__question-content">
          <span class="questions-panel__question-number">{{ index + 1 }}</span>
          <span
            :id="`question-${question.id}-prompt`"
            class="questions-panel__question-text"
          >
            {{ question.promptToken ? $t(question.promptToken) : question.prompt }}
          </span>
          <span
            v-if="question.answer"
            class="questions-panel__answer-badge"
          >
            <template v-if="isAgeQuestion && numberInputValue">
              {{ numberInputValue }} {{ $t(`units.${ageUnit}.short`) }}
            </template>
            <template v-else>
              {{ answerToken(question.answer)
                ? $t(answerToken(question.answer) || '') : question.answer }}
            </template>
          </span>
        </span>
        <span class="questions-panel__chevron-target" aria-hidden="true">
          <span
            class="questions-panel__chevron"
            :class="{ 'questions-panel__chevron--open': question.expanded }"
          >
            &#8964;
          </span>
        </span>
      </button>
      <button
        v-if="media"
        type="button"
        class="questions-panel__media-toggle"
        :aria-expanded="String(isMediaVisible)"
        :aria-controls="mediaId"
        :aria-labelledby="`question-${question.id}-media-label question-${question.id}-prompt`"
        @click="isMediaVisible = !isMediaVisible"
      >
        <svg
          class="questions-panel__media-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <path :d="mediaIcon" />
        </svg>
        <span :id="`question-${question.id}-media-label`">{{ $t(mediaToggleToken) }}</span>
      </button>
    </div>

    <div
      v-if="media"
      v-show="isMediaVisible"
      :id="mediaId"
      class="questions-panel__media"
    >
      <guided-mode-visual
        v-if="isMediaVisible"
        :media="media"
        :playback-rate="mediaPlaybackRate"
        :fallback-icon="mdiImageOutline"
        size="default"
        :interactive="false"
      />
    </div>

    <p
        v-if="isUnanswered"
        :id="`question-${question.id}-unanswered`"
        class="questions-panel__required-message"
        role="alert"
    >
      {{ $t('app.assessment_mode.text.input.question_unanswered') }}
    </p>

    <div
      v-if="question.expanded"
      :id="`question-${question.id}-answer`"
      class="questions-panel__answer"
    >
      <template v-if="question.input === 'blood_pressure'">
        <div class="questions-panel__number-container">
          <blood-pressure-input
            :value="question.answer"
            :input-id="`question-${question.id}`"
            :label="$t(question.promptToken)"
            :ranges="question.bloodPressureOptions || null"
            :unit="question.bloodPressureOptions ? question.bloodPressureOptions.unit : 'mmHg'"
            :systolic-label="$t('questions.blood_pressure.input_label.systolic')"
            :diastolic-label="$t('questions.blood_pressure.input_label.diastolic')"
            :systolic-invalid="bloodPressurePartInvalid('systolic')"
            :diastolic-invalid="bloodPressurePartInvalid('diastolic')"
            :systolic-warning="bloodPressurePartWarning('systolic')"
            :diastolic-warning="bloodPressurePartWarning('diastolic')"
            @input="setTypedAnswer"
          />
          <div
            v-if="displayedValidation"
            class="questions-panel__error"
            :class="{ 'questions-panel__error--warning': isWarningMessage }"
            role="alert"
          >
            {{ $t(displayedValidation.messageToken || '') }}
          </div>
        </div>
      </template>

      <template v-else-if="question.type === 'option'">
        <button
          v-for="option in optionResponses"
          :key="option.value"
          type="button"
          class="questions-panel__option"
          data-analytics-skip
          :class="{ 'questions-panel__option--selected': question.answer === option.value }"
          @click="selectOptionAnswer(option.value)"
        >
          {{ $t(option.token) }}
        </button>
      </template>

      <template v-else-if="question.type === 'number'">
        <div class="questions-panel__number-container">
          <div class="questions-panel__number-field">
            <input
                class="questions-panel__field-input questions-panel__field-input--compact"
                :class="{
                  'questions-panel__field-input--error': displayedNumberError,
                  'questions-panel__field-input--warning': isWarningMessage,
                }"
                type="number"
                :value="numberInputValue"
                :min="isAgeQuestion ? undefined : question.options.min"
                :max="isAgeQuestion ? undefined : question.options.max"
                :step="numberStep(question.options.decimalPlaces)"
                @focus="onNumberFocus"
                @blur="onNumberBlur"
                @input="onNumberInput"
            />
            <select
                v-if="isAgeQuestion"
                class="questions-panel__unit-select"
                :value="ageUnit"
                @change="onAgeUnitChange"
            >
              <option value="days">{{ $t('units.days.short') }}</option>
              <option value="months">{{ $t('units.months.short') }}</option>
              <option value="years">{{ $t('units.years.short') }}</option>
            </select>
            <span
                v-else-if="question.options.unit"
                class="questions-panel__unit-label"
            >{{ $t(`units.${question.options.unit}.short`) }}</span>
          </div>
          <div
            v-if="displayedNumberError"
            class="questions-panel__error"
            :class="{ 'questions-panel__error--warning': isWarningMessage }"
            role="alert"
          >
            {{ $t(displayedNumberError, { min: question.options.min, max: question.options.max }) }}
          </div>
        </div>
        <button
          v-if="question.options.allowUnknown"
          type="button"
          class="questions-panel__option"
          data-analytics-skip
          :class="{ 'questions-panel__option--selected': question.answer === UNKNOWN_ANSWER }"
          @click="selectUnknownNumberAnswer"
        >
          {{ $t('app.assessment_mode.text.input.option.value.unknown') }}
        </button>
      </template>

      <template v-else-if="question.type === 'date'">
        <input
          class="questions-panel__field-input questions-panel__field-input--compact"
          :class="{ 'questions-panel__field-input--error': dateError }"
          type="date"
          :value="dateInputValue"
          :min="dateRange ? dateRange.min : undefined"
          :max="dateRange ? dateRange.max : undefined"
          @input="onDateInput"
        />
        <div v-if="dateError && dateRange" class="questions-panel__error">
          {{ $t(dateError, dateErrorParams || undefined) }}
        </div>
      </template>

      <template v-else>
        <textarea
          class="questions-panel__field-textarea"
          :value="question.answer"
          rows="3"
          @input="onTextInput"
        ></textarea>
      </template>
    </div>
  </article>
</template>

<script lang="ts">
import { computed, defineComponent, getCurrentInstance, PropType, ref, watch } from 'vue';
import BloodPressureInput from '@/components/assessment-inputs/BloodPressureInput.vue';
import { mdiImageOutline, mdiPlayBoxOutline } from '@mdi/js';
import GuidedModeVisual from '../../../guided-mode/components/GuidedModeVisual.vue';
import { useGuidedModeStore } from '../../../guided-mode/stores/guidedMode';
import { usePatientStore } from '../../stores/patient.store';
import { questionMediaFor } from '../../utils/question-media';
import type { QuestionItem, ResponseOption } from '../../types/text-mode.types';
import { useQuestionStore } from '../../stores/question.store';
import {
  buildDateRangeFromOffsets,
  formatDateRangeValue,
  isDateWithinRange,
} from './QuestionCard.utils';

const FALLBACK_OPTIONS: ResponseOption[] = [
  { value: 'Yes', token: 'app.assessment_mode.text.input.option.yes' },
  { value: 'No', token: 'app.assessment_mode.text.input.option.no' },
  { value: 'Unknown', token: 'app.assessment_mode.text.input.option.unknown' },
];
const UNKNOWN_ANSWER = 'Unknown';
type AgeUnit = 'days' | 'months' | 'years';

export default defineComponent({
  name: 'QuestionCard',

  components: {
    BloodPressureInput,
    GuidedModeVisual,
  },

  props: {
    question: {
      type: Object as PropType<QuestionItem>,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isUnanswered: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const questionStore = useQuestionStore();
    const guidedModeStore = useGuidedModeStore();
    const patientStore = usePatientStore();
    const isMediaVisible = ref(false);
    const patientType = computed(() => patientStore.patient?.type);
    const media = computed(() => questionMediaFor(
      patientType.value
        ? guidedModeStore.clinicalContent.assessmentDefinitions[patientType.value] : null,
      props.question.id,
      props.question.promptToken,
    ));
    const mediaPlaybackRate = computed(() => (media.value?.type === 'animation'
      ? media.value.playbackRates?.[props.question.answer] ?? 1 : 1));
    const mediaId = computed(() => `question-${props.question.id}-media`);
    const mediaIcon = computed(() => media.value?.type === 'animation'
      ? mdiPlayBoxOutline : mdiImageOutline);
    const mediaToggleToken = computed(() => isMediaVisible.value
      ? 'app.assessment_mode.text.media.hide' : 'app.assessment_mode.text.media.show');
    watch(
      () => JSON.stringify([
        props.question.id, patientType.value, media.value?.type, media.value?.asset,
      ]),
      () => { isMediaVisible.value = false; },
      { flush: 'sync' },
    );
    const numberError = ref<string>('');
    const dateError = ref<string>('');
    const numberInputValue = ref<string>('');
    const dateInputValue = ref<string>('');
    const ageUnit = ref<AgeUnit>('years');
    const isNumberInputFocused = ref(false);
    const pendingNumberAnswer = ref<string | null>(null);
    const pendingInvalidDateInput = ref(false);
    const isAgeQuestion = computed<boolean>(() => props.question.id === 'age');
    const questionValidation = computed(() => questionStore.validationFor(props.question.id));
    const displayedValidation = computed(() => {
      const validation = questionValidation.value;
      if (!validation || validation.severity === 'normal') return null;
      if (validation.severity === 'warning'
        && !questionStore.isWarningPresented(props.question.id)) return null;
      return validation;
    });
    const displayedNumberError = computed(() => {
      if (numberError.value) return numberError.value;
      return displayedValidation.value?.messageToken ?? '';
    });
    const isWarningMessage = computed(() => {
      return !numberError.value && displayedValidation.value?.severity === 'warning';
    });
    const optionResponses = computed<ResponseOption[]>(() => {
      if (props.question.type !== 'option') {
        return FALLBACK_OPTIONS;
      }
      return props.question.responses?.length ? props.question.responses : FALLBACK_OPTIONS;
    });

    const emitToggle = (): void => {
      emit('toggle', props.question.id);
    };

    const emitAnswer = (answer: string): void => {
      emit('answer', {
        questionId: props.question.id,
        answer,
      });
    };

    const selectOptionAnswer = (answer: string): void => {
      emitAnswer(answer);
      emit('toggle', props.question.id);
    };

    const setTypedAnswer = (answer: string): void => {
      emitAnswer(answer);
    };

    const numberStep = (decimalPlaces: number): string => {
      if (decimalPlaces <= 0) {
        return '1';
      }
      return (1 / (10 ** decimalPlaces)).toString();
    };

    const dateRange = computed<{ min: string; max: string } | null>(() => {
      return props.question.type === 'date'
        ? buildDateRangeFromOffsets(props.question.dateOptions)
        : null;
    });

    const dateErrorParams = computed<Record<string, string> | null>(() => {
      if (!dateRange.value) {
        return null;
      }

      const locale = vm?.proxy?.$i18n?.locale || 'en';
      return {
        min: formatDateRangeValue(dateRange.value.min, locale),
        max: formatDateRangeValue(dateRange.value.max, locale),
      };
    });

    const answerToken = (answer: string): string | null => {
      return optionResponses.value.find((option) => option.value === answer)?.token || null;
    };

    const toAgeInDays = (value: string, unit: AgeUnit): number => {
      const parsed = Number.parseFloat(value);
      if (Number.isNaN(parsed)) return Number.NaN;

      if (unit === 'days') return Math.round(parsed);
      if (unit === 'months') return Math.round(parsed * 30);
      return Math.round(parsed * 365);
    };

    const fromAgeInDays = (ageInDays: number, unit: AgeUnit): string => {
      if (Number.isNaN(ageInDays) || ageInDays < 0) return '';
      if (unit === 'days') return String(Math.round(ageInDays));
      if (unit === 'months') return String(Math.round(ageInDays / 30));
      return String(Math.round(ageInDays / 365));
    };

    const emitAgeInDays = (): void => {
      if (!numberInputValue.value) {
        setTypedAnswer('');
        return;
      }

      const ageInDays = toAgeInDays(numberInputValue.value, ageUnit.value);
      if (Number.isNaN(ageInDays)) {
        numberError.value = 'app.assessment_mode.number.input.error.invalid_number';
        return;
      }

      numberError.value = '';
      setTypedAnswer(String(ageInDays));
    };

    const onNumberInput = (event: Event): void => {
      const target = event.target as HTMLInputElement | null;
      const value = target?.value || '';
      numberInputValue.value = value;

      if (!value) {
        numberError.value = '';
        setTypedAnswer(value);
        return;
      }

      const num = Number.parseFloat(value);
      if (Number.isNaN(num)) {
        numberError.value = 'app.assessment_mode.number.input.error.invalid_number';
        return;
      }

      if (isAgeQuestion.value) {
        emitAgeInDays();
        return;
      }

      numberError.value = '';
      setTypedAnswer(value);
    };

    const bloodPressurePartInvalid = (part: 'systolic' | 'diastolic'): boolean => {
      const validation = displayedValidation.value;
      if (!validation || validation.severity === 'warning') return false;
      if (validation.code === 'invalid_relationship' || validation.code === 'invalid_format'
        || validation.code === 'invalid_configuration') return true;
      return validation.parts?.[part]?.severity === 'blocking';
    };

    const bloodPressurePartWarning = (part: 'systolic' | 'diastolic'): boolean => {
      if (!isWarningMessage.value) return false;
      return displayedValidation.value?.parts?.[part]?.severity === 'warning';
    };

    const onNumberFocus = (): void => {
      isNumberInputFocused.value = true;
    };

    const onNumberBlur = (): void => {
      isNumberInputFocused.value = false;
      if (pendingNumberAnswer.value !== null) {
        numberInputValue.value = pendingNumberAnswer.value;
        pendingNumberAnswer.value = null;
      }
    };

    const onAgeUnitChange = (event: Event): void => {
      const target = event.target as HTMLSelectElement | null;
      const selectedUnit = target?.value;
      if (selectedUnit === 'days' || selectedUnit === 'months' || selectedUnit === 'years') {
        ageUnit.value = selectedUnit;
        if (numberInputValue.value) {
          emitAgeInDays();
        }
      }
    };

    watch(
      () => [props.question.type, props.question.answer] as const,
      ([type, answer]) => {
        if (type !== 'number') {
          pendingNumberAnswer.value = null;
          return;
        }

        if (isAgeQuestion.value) {
          if (isNumberInputFocused.value) {
            return;
          }

          const answerValue = Number.parseFloat(String(answer ?? ''));
          numberInputValue.value = Number.isNaN(answerValue)
            ? ''
            : fromAgeInDays(answerValue, ageUnit.value);
          return;
        }

        const nextAnswer = String(answer ?? '');
        if (isNumberInputFocused.value) {
          pendingNumberAnswer.value = nextAnswer;
          return;
        }

        pendingNumberAnswer.value = null;
        numberInputValue.value = nextAnswer;
      },
      { immediate: true },
    );

    watch(
      () => [props.question.type, props.question.answer] as const,
      ([type, answer]) => {
        if (type !== 'date') {
          dateError.value = '';
          dateInputValue.value = '';
          pendingInvalidDateInput.value = false;
          return;
        }

        const nextAnswer = String(answer ?? '');
        if (pendingInvalidDateInput.value && !nextAnswer) {
          pendingInvalidDateInput.value = false;
          return;
        }

        dateError.value = '';
        dateInputValue.value = nextAnswer;
      },
      { immediate: true },
    );

    const onDateInput = (event: Event): void => {
      const target = event.target as HTMLInputElement | null;
      const value = target?.value || '';
      dateInputValue.value = value;

      if (!value) {
        dateError.value = '';
        pendingInvalidDateInput.value = false;
        setTypedAnswer('');
        return;
      }

      if (!isDateWithinRange(value, dateRange.value)) {
        dateError.value = 'app.assessment_mode.number.input.error.out_of_range';
        if (props.question.answer) {
          pendingInvalidDateInput.value = true;
          setTypedAnswer('');
        }
        return;
      }

      dateError.value = '';
      pendingInvalidDateInput.value = false;
      setTypedAnswer(value);
    };

    const onTextInput = (event: Event): void => {
      const target = event.target as HTMLTextAreaElement | null;
      setTypedAnswer(target?.value || '');
    };

    const selectUnknownNumberAnswer = (): void => {
      numberError.value = '';
      numberInputValue.value = '';
      setTypedAnswer(UNKNOWN_ANSWER);
    };

    return {
      isMediaVisible,
      media,
      mediaPlaybackRate,
      mediaId,
      mediaIcon,
      mediaToggleToken,
      mdiImageOutline,
      ageUnit,
      answerToken,
      dateError,
      dateErrorParams,
      dateInputValue,
      dateRange,
      displayedNumberError,
      displayedValidation,
      emitToggle,
      isAgeQuestion,
      isWarningMessage,
      numberInputValue,
      numberError,
      numberStep,
      onAgeUnitChange,
      onNumberBlur,
      onNumberFocus,
      onDateInput,
      onNumberInput,
      onTextInput,
      optionResponses,
      bloodPressurePartInvalid,
      bloodPressurePartWarning,
      selectUnknownNumberAnswer,
      selectOptionAnswer,
      setTypedAnswer,
      UNKNOWN_ANSWER,
    };
  },
});
</script>
